import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
    List,
    Datagrid,
    TextField as TextFieldRa,
    FunctionField,
    Create,
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    EditButton,
    DeleteButton,
    ShowButton,
    BooleanInput,
    DateInput,
    useShowController,
    useNotify,
    BooleanField,
    TopToolbar,
    ListButton,
} from 'react-admin'
import CampaignMuiIcon from '@mui/icons-material/Campaign'
import { Button, CircularProgress, Chip, TextField } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import api from '../../../Services/Api'

export const CampaignIcon = CampaignMuiIcon

const DESTINATION_TEXT = {
    2: 'Brasil',
    3: 'Oceania',
    5: 'Europa',
    6: 'Brasil Receptivo (Estrangeiros)',
    7: 'Ásia',
    8: 'América do Norte',
    9: 'América do Sul',
    10: 'América Central',
    11: 'África',
    12: 'Oriente Médio',
    13: 'América Central e Caribe',
    15: 'México',
    16: 'Multiviagens (seguro anual)',
    19: 'Canadá',
    21: 'Internacional (com EUA/Canadá)',
    22: 'Internacional (exceto EUA/Canadá)',
    24: 'Cruzeiro Marítimo América Latina',
    25: 'Cruzeiro Marítimo Europa',
    26: 'Cruzeiro Marítimo Mundo',
}

const DESTINATION_CHOICES = Object.entries(DESTINATION_TEXT).map(([id, name]) => ({
    id: Number(id),
    name,
}))

const CATEGORY_CHOICES = [
    { id: 1, name: 'Econômico' },
    { id: 2, name: 'Intermediário' },
    { id: 3, name: 'Premium' },
]

const formatDate = (value) => {
    if (!value) return '—'
    const d = new Date(value)
    if (isNaN(d)) return value
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const getAuthHeaders = () => {
    try {
        const { token } = JSON.parse(localStorage.getItem('auth'))
        return { Authorization: `Bearer ${token}` }
    } catch {
        return {}
    }
}

export const CampaignList = (props) => (
    <List
        {...props}
        exporter={false}
        title="Campanhas"
        bulkActionButtons={false}
        sort={{ field: 'id', order: 'DESC' }}
    >
        <Datagrid>
            <TextFieldRa source="title" label="Título" />
            <BooleanField source="status" label="Ativo" />
            <FunctionField source="destination" label="Destino" render={(r) => DESTINATION_TEXT[r.destination] || r.destination || '—'} />
            <FunctionField source="startDate" label="Início" render={(r) => formatDate(r.startDate)} />
            <FunctionField source="endDate" label="Fim" render={(r) => formatDate(r.endDate)} />
            <ShowButton label="" />
            <EditButton label="" />
            <DeleteButton label="" />
        </Datagrid>
    </List>
)

export const CampaignCreate = (props) => (
    <Create {...props} title="Nova Campanha">
        <SimpleForm redirect="list">
            <TextInput source="title" label="Título" fullWidth />
            <TextInput source="description" label="Descrição" fullWidth multiline />
            <SelectInput source="destination" label="Destino" choices={DESTINATION_CHOICES} />
            <SelectInput source="category" label="Categoria" choices={CATEGORY_CHOICES} />
            <TextInput source="startColor" label="Cor Inicial (hex)" />
            <TextInput source="endColor" label="Cor Final (hex)" />
            <TextInput source="textColor" label="Cor do Texto (hex)" />
            <BooleanInput source="status" label="Ativo" defaultValue={true} />
            <DateInput source="startDate" label="Data de Início" />
            <DateInput source="endDate" label="Data de Fim" />
        </SimpleForm>
    </Create>
)

export const CampaignEdit = (props) => (
    <Edit {...props} title="Editar Campanha">
        <SimpleForm redirect="list">
            <TextInput source="title" label="Título" fullWidth />
            <TextInput source="description" label="Descrição" fullWidth multiline />
            <SelectInput source="destination" label="Destino" choices={DESTINATION_CHOICES} />
            <SelectInput source="category" label="Categoria" choices={CATEGORY_CHOICES} />
            <TextInput source="startColor" label="Cor Inicial (hex)" />
            <TextInput source="endColor" label="Cor Final (hex)" />
            <TextInput source="textColor" label="Cor do Texto (hex)" />
            <BooleanInput source="status" label="Ativo" />
            <DateInput source="startDate" label="Data de Início" />
            <DateInput source="endDate" label="Data de Fim" />
        </SimpleForm>
    </Edit>
)

const styles = {
    container: {
        maxWidth: '800px',
        margin: '24px auto',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        padding: '24px 32px',
        color: '#444',
    },
    pageTitle: {
        fontWeight: '700',
        fontSize: '26px',
        marginBottom: '8px',
        borderBottom: '2px solid #ddd',
        paddingBottom: '10px',
        color: '#222',
    },
    section: {
        marginBottom: '32px',
    },
    sectionTitle: {
        fontWeight: '700',
        fontSize: '18px',
        color: '#444',
        marginBottom: '16px',
        borderBottom: '1px solid #eee',
        paddingBottom: '6px',
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        rowGap: '10px',
        alignItems: 'center',
    },
    label: {
        fontWeight: '600',
        color: '#666',
        fontSize: '14px',
    },
    value: {
        color: '#333',
        fontSize: '14px',
    },
    colorSwatch: {
        display: 'inline-block',
        width: '20px',
        height: '20px',
        borderRadius: '4px',
        border: '1px solid #ccc',
        marginRight: '8px',
        verticalAlign: 'middle',
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
    },
    searchResults: {
        border: '1px solid #ddd',
        borderRadius: '4px',
        maxHeight: '220px',
        overflowY: 'auto',
        marginBottom: '16px',
        background: '#fafafa',
    },
    searchResultItem: {
        padding: '10px 14px',
        borderBottom: '1px solid #eee',
        cursor: 'pointer',
        fontSize: '14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    selectedChipsArea: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '16px',
        minHeight: '36px',
    },
    saveBtn: {
        marginTop: '4px',
        background: '#3f51b5',
        color: '#fff',
    },
    productItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderBottom: '1px solid #eee',
        fontSize: '14px',
    },
    productList: {
        border: '1px solid #ddd',
        borderRadius: '4px',
        background: '#fafafa',
        maxHeight: '320px',
        overflowY: 'auto',
    },
    emptyMsg: {
        padding: '16px',
        color: '#aaa',
        fontSize: '14px',
        textAlign: 'center',
    },
    removeBtn: {
        minWidth: 0,
        padding: '4px 8px',
        color: '#e53935',
    },
    topActions: {
        display: 'flex',
        justifyContent: 'flex-start',
        marginBottom: '16px',
    },
    noResultsMsg: {
        padding: '12px 14px',
        color: '#aaa',
        fontSize: '14px',
    },
}

const DEBOUNCE_MS = 400

export const CampaignShow = (props) => {
    const { record } = useShowController(props)
    const notify = useNotify()

    const [campaignProducts, setCampaignProducts] = useState([])
    const [loadingProducts, setLoadingProducts] = useState(false)

    const [searchTerm, setSearchTerm] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const [searching, setSearching] = useState(false)
    const [selectedToAdd, setSelectedToAdd] = useState([])
    const [saving, setSaving] = useState(false)
    const [searched, setSearched] = useState(false)

    const debounceRef = useRef(null)
    const searchContainerRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
                setSearchResults([])
                setSearched(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const fetchProducts = useCallback(async (campaignId) => {
        setLoadingProducts(true)
        try {
            const res = await api.get(`admin/campaigns/${campaignId}`, {
                headers: getAuthHeaders(),
            })
            const campaignData = res.data?.data || res.data
            setCampaignProducts(campaignData?.products || [])
        } catch (err) {
            const msg = err?.response?.data?.message || 'Erro ao carregar produtos da campanha'
            notify(msg, 'error')
        } finally {
            setLoadingProducts(false)
        }
    }, [notify])

    useEffect(() => {
        if (record?.id) {
            fetchProducts(record.id)
        }
    }, [record?.id, fetchProducts])

    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchTerm(value)

        if (debounceRef.current) clearTimeout(debounceRef.current)

        if (!value.trim()) {
            setSearchResults([])
            setSearched(false)
            return
        }

        debounceRef.current = setTimeout(async () => {
            setSearching(true)
            setSearched(true)
            try {
                const res = await api.get(`admin/products`, {
                    headers: getAuthHeaders(),
                    params: { q: value, _start: 0, _end: 20, destinationId: record.destination, active: true },
                })
                const raw = res.data
                let data = []
                if (Array.isArray(raw)) {
                    data = raw
                } else if (Array.isArray(raw?.data)) {
                    data = raw.data
                } else if (Array.isArray(raw?.rows)) {
                    data = raw.rows
                } else if (Array.isArray(raw?.items)) {
                    data = raw.items
                } else if (Array.isArray(raw?.data?.rows)) {
                    data = raw.data.rows
                }
                setSearchResults(data)
            } catch (err) {
                const msg = err?.response?.data?.message || 'Erro ao buscar produtos'
                notify(msg, 'error')
                setSearchResults([])
            } finally {
                setSearching(false)
            }
        }, DEBOUNCE_MS)
    }

    const handleSelectProduct = (product) => {
        const alreadyInCampaign = campaignProducts.some((p) => p.productId === product.id)
        if (alreadyInCampaign) {
            notify('Este produto já está na campanha', 'warning')
            return
        }
        setSelectedToAdd((prev) => {
            if (prev.some((p) => p.id === product.id)) return prev
            return [...prev, product]
        })
    }

    const handleDeselect = (productId) => {
        setSelectedToAdd((prev) => prev.filter((p) => p.id !== productId))
    }

    const handleSave = async () => {
        if (!selectedToAdd.length) return
        setSaving(true)
        try {
            const res = await api.post(
                `admin/campaigns/${record.id}/products`,
                { productsId: selectedToAdd.map((p) => p.id) },
                { headers: getAuthHeaders() }
            )
            const msg = res.data?.message || 'Produtos adicionados com sucesso'
            notify(msg, 'success')
            setSelectedToAdd([])
            setSearchTerm('')
            setSearchResults([])
            setSearched(false)
            await fetchProducts(record.id)
        } catch (err) {
            const msg = err?.response?.data?.message || 'Erro ao adicionar produtos'
            notify(msg, 'error')
        } finally {
            setSaving(false)
        }
    }

    const handleRemoveProduct = async (productId) => {
        try {
            const res = await api.delete(`admin/campaigns/${record.id}/products/${productId}`, {
                headers: getAuthHeaders(),
            })
            const msg = res.data?.message || 'Produto removido com sucesso'
            notify(msg, 'success')
            setCampaignProducts((prev) => prev.filter((p) => p.productId !== productId))
        } catch (err) {
            const msg = err?.response?.data?.message || 'Erro ao remover produto'
            notify(msg, 'error')
        }
    }

    if (!record) return null

    return (
        <div>
            <TopToolbar>
                <ListButton label="Voltar" />
            </TopToolbar>

            <div style={styles.container}>
                <h2 style={styles.pageTitle}>Campanha: {record.title}</h2>

                {/* Informações da Campanha */}
                <section style={styles.section}>
                    <h3 style={styles.sectionTitle}>Informações</h3>
                    <div style={styles.infoGrid}>
                        <span style={styles.label}>ID</span>
                        <span style={styles.value}>{record.id}</span>

                        <span style={styles.label}>Título</span>
                        <span style={styles.value}>{record.title}</span>

                        <span style={styles.label}>Descrição</span>
                        <span style={styles.value}>{record.description}</span>

                        <span style={styles.label}>Status</span>
                        <span style={styles.value}>{record.status ? 'Ativo' : 'Inativo'}</span>

                        <span style={styles.label}>Destino</span>
                        <span style={styles.value}>
                            {DESTINATION_TEXT[record.destination] || record.destination || '—'}
                        </span>

                        <span style={styles.label}>Categoria</span>
                        <span style={styles.value}>
                            {CATEGORY_CHOICES.find((c) => c.id === record.category)?.name || record.category || '—'}
                        </span>

                        <span style={styles.label}>Data de Início</span>
                        <span style={styles.value}>{formatDate(record.startDate)}</span>

                        <span style={styles.label}>Data de Fim</span>
                        <span style={styles.value}>{formatDate(record.endDate)}</span>

                        <span style={styles.label}>Cor Inicial</span>
                        <span style={styles.value}>
                            {record.startColor && (
                                <span
                                    style={{ ...styles.colorSwatch, backgroundColor: record.startColor }}
                                />
                            )}
                            {record.startColor}
                        </span>

                        <span style={styles.label}>Cor Final</span>
                        <span style={styles.value}>
                            {record.endColor && (
                                <span
                                    style={{ ...styles.colorSwatch, backgroundColor: record.endColor }}
                                />
                            )}
                            {record.endColor}
                        </span>

                        <span style={styles.label}>Cor do Texto</span>
                        <span style={styles.value}>
                            {record.textColor && (
                                <span
                                    style={{ ...styles.colorSwatch, backgroundColor: record.textColor }}
                                />
                            )}
                            {record.textColor}
                        </span>
                    </div>
                </section>

                {/* Gerenciar Produtos */}
                <section style={styles.section}>
                    <h3 style={styles.sectionTitle}>Adicionar Produtos</h3>

                    <div ref={searchContainerRef} style={{ position: 'relative' }}>
                    <div style={styles.searchBox}>
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Pesquisar produto por nome..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            style={{ flex: 1 }}
                            InputProps={{
                                endAdornment: searching ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <SearchIcon style={{ color: '#aaa' }} />
                                ),
                            }}
                        />
                    </div>

                    {searchTerm.trim() && (
                        <div style={{ ...styles.searchResults, position: 'absolute', width: '100%', zIndex: 10 }}>
                            {searching && (
                                <div style={styles.noResultsMsg}>Buscando...</div>
                            )}
                            {!searching && searched && searchResults.length === 0 && (
                                <div style={styles.noResultsMsg}>Nenhum produto encontrado.</div>
                            )}
                            {!searching &&
                                searchResults.map((product, idx) => {
                                    const alreadySelected = selectedToAdd.some((p) => p.id === product.id)
                                    const alreadyInCampaign = campaignProducts.some((p) => p.productId === product.id)
                                    return (
                                        <div
                                            key={product.id ?? idx}
                                            style={{
                                                ...styles.searchResultItem,
                                                background: alreadySelected
                                                    ? '#e8eaf6'
                                                    : alreadyInCampaign
                                                    ? '#f1f8e9'
                                                    : 'transparent',
                                                cursor: alreadyInCampaign ? 'default' : 'pointer',
                                            }}
                                            onClick={() => !alreadyInCampaign && handleSelectProduct(product)}
                                        >
                                            <span>
                                                <strong>#{product.id}</strong> — {product.name || product.title}
                                            </span>
                                            {alreadyInCampaign && (
                                                <span style={{ fontSize: '12px', color: '#66bb6a' }}>
                                                    já adicionado
                                                </span>
                                            )}
                                            {!alreadyInCampaign && alreadySelected && (
                                                <span style={{ fontSize: '12px', color: '#5c6bc0' }}>
                                                    selecionado
                                                </span>
                                            )}
                                        </div>
                                    )
                                })}
                        </div>
                    )}
                    </div>{/* fim searchContainerRef */}

                    {selectedToAdd.length > 0 && (
                        <>
                            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#666' }}>
                                Produtos selecionados para adicionar:
                            </div>
                            <div style={styles.selectedChipsArea}>
                                {selectedToAdd.map((product, idx) => (
                                    <Chip
                                        key={product.id ?? idx}
                                        label={`#${product.id} — ${product.name || product.title}`}
                                        onDelete={() => handleDeselect(product.id)}
                                        color="primary"
                                        size="small"
                                    />
                                ))}
                            </div>
                            <Button
                                variant="contained"
                                style={styles.saveBtn}
                                onClick={handleSave}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : null}
                            >
                                {saving ? 'Salvando...' : 'Salvar Produtos'}
                            </Button>
                        </>
                    )}
                </section>

                {/* Produtos da Campanha */}
                <section style={styles.section}>
                    <h3 style={styles.sectionTitle}>
                        Produtos da Campanha{' '}
                        <span style={{ fontWeight: '400', fontSize: '14px', color: '#888' }}>
                            ({campaignProducts.length})
                        </span>
                    </h3>

                    {loadingProducts ? (
                        <div style={{ textAlign: 'center', padding: '16px' }}>
                            <CircularProgress size={24} />
                        </div>
                    ) : campaignProducts.length === 0 ? (
                        <div style={styles.emptyMsg}>Nenhum produto adicionado a esta campanha.</div>
                    ) : (
                        <div style={styles.productList}>
                            {campaignProducts.map((product, idx) => (
                                <div key={product.productId ?? idx} style={styles.productItem}>
                                    <span>
                                        <strong>#{product.productId}</strong> — {product.name}
                                    </span>
                                    <Button
                                        size="small"
                                        style={styles.removeBtn}
                                        onClick={() => handleRemoveProduct(product.productId)}
                                        startIcon={<CloseIcon fontSize="small" />}
                                    >
                                        Remover
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    )
}